import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { StorageService } from '../services/storage.service';
import { AuthModalService } from '../services/auth-modal.service';
import { ACTIVE_TENANT } from '../tenant/tenants';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private notificationService = inject(NotificationService);
  private storageService = inject(StorageService);
  private authModalService = inject(AuthModalService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        return throwError(() => error);
      })
    );
  }

  private handleError(error: HttpErrorResponse): void {
    switch (error.status) {
      case 401:
        // Unauthorized - clear auth and redirect to login
        this.clearAuthStorage();
        if (ACTIVE_TENANT.features.accounts) {
          this.authModalService.open('/home');
          this.notificationService.showError('Your session has expired. Please log in again.');
        } else {
          this.notificationService.showError('Something went wrong. Please try again.');
        }
        break;
        
      case 403:
        // Forbidden - show access denied message
        this.notificationService.showError('Access denied. You do not have permission to perform this action.');
        break;
        
      case 404:
        // Not found - show resource not found message
        this.notificationService.showError('The requested resource was not found.');
        break;
        
      case 409:
        // Conflict - show conflict message
        this.notificationService.showError('A conflict occurred. The resource may have been modified by another user.');
        break;
        
      case 422:
        // Validation error - show validation message
        if (error.error?.message) {
          this.notificationService.showError(error.error.message);
        } else {
          this.notificationService.showError('Please check your input and try again.');
        }
        break;
        
      case 429:
        // Rate limiting - show rate limit message
        this.notificationService.showError('Too many requests. Please wait a moment and try again.');
        break;
        
      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors - show generic server error message
        this.notificationService.showError('A server error occurred. Please try again later.');
        break;
        
      case 0:
        // Network error - show network error message
        this.notificationService.showError('Network error. Please check your internet connection.');
        break;
        
      default:
        // Generic error message for other status codes
        this.notificationService.showError('An unexpected error occurred. Please try again.');
        break;
    }
  }

  private clearAuthStorage(): void {
    this.storageService.removeItem('access_token');
    this.storageService.removeItem('refresh_token');
    this.storageService.removeItem('user');
    this.storageService.removeItem('user_context');
  }
}
